import React, { useState } from 'react';
import PropTypes from 'prop-types';
import readXlsxFile from 'read-excel-file';
import {
  EuiButton,
  EuiCallOut,
  EuiFilePicker,
  EuiPanel,
  EuiProgress,
  EuiText,
} from '@elastic/eui';

const HEADER_ALIASES = {
  id: ['id', '經文id', '经文id'],
  book: ['書卷編號', '书卷编号', 'booknumber', 'book'],
  chapter: ['章', '章號', '章号', 'chapter'],
  verseNumber: ['節', '节', '節號', '节号', 'versenumber'],
  text: ['經文', '经文', 'verse', 'text'],
};

const normalizeHeader = value => String(value ?? '').trim().toLowerCase().replace(/[\s_-]/g, '');
const numericId = value => {
  const match = String(value ?? '').match(/\d+$/);
  return match ? Number(match[0]) : null;
};
const numericCell = value => {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
};
const findColumn = (headers, aliases) => {
  const values = new Set(aliases.map(normalizeHeader));
  return headers.find(item => values.has(item.value))?.column ?? null;
};

const buildVerseDocument = (verse, text) => ({
  id: numericId(verse.id),
  doctype: '經文',
  title: text,
  book_number: verse.booknumber,
  chapter_code_name: verse.chaptercodename,
  chapter_name: verse.chaptername,
  chapter_number: verse.chapternumber,
  chapter_verse_number: verse.chapterversenumber,
  verse_number: verse.versenumber,
  verse: verse.verse,
  verse_id: verse.verse_id,
  tag: verse.tag,
});

const VerseExcelUpdater = ({ verses, onVersesUpdated }) => {
  const [pickerKey, setPickerKey] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [message, setMessage] = useState('');

  const analyzeFile = async files => {
    const file = files?.[0];
    setAnalysis(null);
    setMessage('');
    if (!file) return;

    setParsing(true);
    try {
      const rows = await readXlsxFile(file, { trim: false });
      if (!rows.length) throw new Error('Excel 檔案中沒有資料。');

      let headerRow = -1;
      let columns = null;
      for (let rowIndex = 0; rowIndex < Math.min(rows.length, 10); rowIndex += 1) {
        const headers = rows[rowIndex].map((value, column) => ({ column, value: normalizeHeader(value) }));
        const candidate = {
          id: findColumn(headers, HEADER_ALIASES.id),
          book: findColumn(headers, HEADER_ALIASES.book),
          chapter: findColumn(headers, HEADER_ALIASES.chapter),
          verseNumber: findColumn(headers, HEADER_ALIASES.verseNumber),
          text: findColumn(headers, HEADER_ALIASES.text),
        };
        if (Object.values(candidate).every(value => value !== null)) {
          headerRow = rowIndex;
          columns = candidate;
          break;
        }
      }
      if (!columns) throw new Error('找不到「ID、書卷編號、章、節、經文」欄。請使用系統生成的 Excel 格式。');

      const byReference = new Map(
        verses.map(verse => [
          String(Number(verse.booknumber)) + ':' + String(Number(verse.chapternumber)) + ':' + String(Number(verse.versenumber)),
          verse,
        ])
      );
      const byId = new Map(
        verses.map(verse => [String(numericId(verse.id)), verse])
      );
      const changes = [];
      const conflicts = [];
      const unmatched = [];
      let unchanged = 0;
      let emptyIgnored = 0;

      for (let rowIndex = headerRow + 1; rowIndex < rows.length; rowIndex += 1) {
        const row = rows[rowIndex] || [];
        const excelId = numericId(row[columns.id]);
        const book = numericCell(row[columns.book]);
        const chapter = numericCell(row[columns.chapter]);
        const verseNumber = numericCell(row[columns.verseNumber]);
        const rawText = row[columns.text];
        const nextText = rawText === null || rawText === undefined ? '' : String(rawText);
        if (!excelId && !book && !chapter && !verseNumber && !nextText) continue;

        const reference = book && chapter && verseNumber ? String(book) + ':' + String(chapter) + ':' + String(verseNumber) : '';
        const currentById = excelId ? byId.get(String(excelId)) : null;
        const currentByReference = reference ? byReference.get(reference) : null;
        const current = currentById || currentByReference;
        if (!current) {
          unmatched.push({ rowNumber: rowIndex + 1, reference: reference || '(資料不完整)' });
          continue;
        }
        if (excelId !== numericId(current.id)) {
          conflicts.push({
            rowNumber: rowIndex + 1,
            reference,
            reason: 'Excel ID ' + (excelId ?? '(空)') + ' 與目前 ID ' + numericId(current.id) + ' 不一致',
          });
          continue;
        }
        const currentReference = String(Number(current.booknumber)) + ':'
          + String(Number(current.chapternumber)) + ':'
          + String(Number(current.versenumber));
        if (reference !== currentReference) {
          conflicts.push({
            rowNumber: rowIndex + 1,
            reference,
            reason: 'ID 對應的目前經節是 ' + currentReference,
          });
          continue;
        }
        if (nextText.length === 0) {
          emptyIgnored += 1;
          continue;
        }
        if (nextText === String(current.title ?? '')) {
          unchanged += 1;
          continue;
        }
        changes.push({
          current,
          rowNumber: rowIndex + 1,
          reference,
          currentText: String(current.title ?? ''),
          nextText,
        });
      }

      setAnalysis({ fileName: file.name, changes, conflicts, unmatched, unchanged, emptyIgnored });
      setMessage(changes.length
        ? '已找到 ' + changes.length + ' 節需要更新，請先核對預覽。'
        : '沒有發現需要更新的經文。');
    } catch (error) {
      console.error('讀取經文 Excel 失敗:', error);
      setMessage('讀取失敗：' + error.message);
    } finally {
      setParsing(false);
      setPickerKey(value => value + 1);
    }
  };

  const submitUpdates = async () => {
    const changes = analysis?.changes || [];
    if (!changes.length || submitting) return;
    if (analysis.conflicts.length || analysis.unmatched.length) {
      setMessage('仍有 ID 或章節衝突，請先修正 Excel 後重新選擇檔案。');
      return;
    }
    if (!window.confirm('確認把 ' + changes.length + ' 節經文更新到伺服器嗎？此操作會直接修改搜尋資料。')) return;

    const postURL = process.env.REACT_APP_BibleDocURL;
    const postKey = process.env.REACT_APP_PostKey;
    const batchSize = 50;
    const total = Math.ceil(changes.length / batchSize);
    const succeeded = new Map();
    const failures = [];

    setSubmitting(true);
    setProgress({ completed: 0, total });
    setMessage('正在分批更新經文...');

    for (let index = 0; index < changes.length; index += batchSize) {
      const batch = changes.slice(index, index + batchSize);
      try {
        const response = await fetch(postURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: postKey,
          },
          body: JSON.stringify(batch.map(change => buildVerseDocument(change.current, change.nextText))),
        });
        if (!response.ok) throw new Error(response.status + ' ' + response.statusText + ': ' + await response.text());
        const results = await response.json();
        batch.forEach((change, batchIndex) => {
          const result = Array.isArray(results) ? results[batchIndex] : null;
          if (result?.errors?.length) {
            failures.push(change.reference + ': ' + result.errors.join(', '));
          } else {
            succeeded.set(String(change.current.id), change.nextText);
          }
        });
      } catch (error) {
        batch.forEach(change => failures.push(change.reference + ': ' + error.message));
      }
      setProgress({ completed: Math.floor(index / batchSize) + 1, total });
    }

    onVersesUpdated(succeeded);
    setAnalysis(previous => previous
      ? { ...previous, changes: previous.changes.filter(change => !succeeded.has(String(change.current.id))) }
      : previous);
    setMessage(failures.length
      ? '成功更新 ' + succeeded.size + ' 節，失敗 ' + failures.length + ' 節。第一個錯誤：' + failures[0]
      : '已成功更新 ' + succeeded.size + ' 節經文。');
    setSubmitting(false);
  };

  const hasErrors = Boolean(analysis?.conflicts.length || analysis?.unmatched.length);

  return (
    <EuiPanel paddingSize="m" style={{ marginBottom: 16, textAlign: 'left' }}>
      <h3 style={{ marginTop: 0 }}>從 Excel 批量校對經文</h3>
      <EuiText size="s">
        <p>請使用系統生成的 Excel，只修改「經文」欄。系統依書卷編號、章、節定位，並核對 ID；提交前會顯示差異。</p>
      </EuiText>
      <EuiFilePicker
        key={pickerKey}
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={analyzeFile}
        disabled={parsing || submitting}
        initialPromptText="選擇或拖放經文 Excel"
        fullWidth
      />
      {parsing && <EuiProgress size="xs" color="primary" />}
      {message && (
        <EuiCallOut
          title={message}
          color={message.startsWith('讀取失敗') || message.includes('失敗') || hasErrors ? 'danger' : 'primary'}
          size="s"
          style={{ marginTop: 10 }}
        />
      )}

      {analysis && (
        <div style={{ marginTop: 12 }}>
          <EuiText size="s">
            <p>
              檔案：{analysis.fileName}；待更新：{analysis.changes.length}；
              未匹配：{analysis.unmatched.length}；衝突：{analysis.conflicts.length}；
              未變更：{analysis.unchanged}；空白已忽略：{analysis.emptyIgnored}。
            </p>
          </EuiText>
          {hasErrors && (
            <EuiCallOut title="Excel 有不能安全定位的資料，本次不允許提交。" color="warning" size="s">
              {[...analysis.conflicts, ...analysis.unmatched].slice(0, 10).map(item => (
                <p key={String(item.rowNumber) + '-' + item.reference}>
                  第 {item.rowNumber} 行，{item.reference}：{item.reason || '找不到目前經文'}
                </p>
              ))}
            </EuiCallOut>
          )}
          {analysis.changes.length > 0 && (
            <>
              <div style={{ overflow: 'auto', maxHeight: 380, margin: '12px 0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      {['Excel 行', '經文', '目前內容', '更新後內容'].map(label => (
                        <th key={label} style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #d3dae6' }}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.changes.slice(0, 100).map(change => (
                      <tr key={String(change.current.id) + '-' + String(change.rowNumber)}>
                        <td style={{ padding: 6, verticalAlign: 'top' }}>{change.rowNumber}</td>
                        <td style={{ padding: 6, verticalAlign: 'top', whiteSpace: 'nowrap' }}>{change.reference}</td>
                        <td style={{ padding: 6, verticalAlign: 'top', minWidth: 260 }}>{change.currentText || '(空)'}</td>
                        <td style={{ padding: 6, verticalAlign: 'top', minWidth: 260, background: '#fff2cc' }}>{change.nextText}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {analysis.changes.length > 100 && <EuiText size="xs"><p>預覽只顯示前 100 筆，提交時會處理全部變更。</p></EuiText>}
              <EuiButton fill onClick={submitUpdates} isLoading={submitting} isDisabled={hasErrors}>
                確認並提交 {analysis.changes.length} 節更新
              </EuiButton>
            </>
          )}
          {submitting && progress.total > 0 && (
            <div style={{ marginTop: 10 }}>
              <EuiProgress value={progress.completed} max={progress.total} size="m" color="primary" />
              <EuiText size="xs"><p>已完成批次：{progress.completed}/{progress.total}</p></EuiText>
            </div>
          )}
        </div>
      )}
    </EuiPanel>
  );
};

VerseExcelUpdater.propTypes = {
  verses: PropTypes.arrayOf(PropTypes.object).isRequired,
  onVersesUpdated: PropTypes.func.isRequired,
};

export default VerseExcelUpdater;
